import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, ShieldOff, X } from "lucide-react";
import api, { apiError, passwordError } from "../lib/api";
import { BrandMark } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";

const roles = ["manager", "mechanic", "office"];

export default function Team() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();
  const [users, setUsers] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "mechanic" });

  const load = useCallback(async () => { const { data } = await api.get("/users"); setUsers(data); }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name || !form.email) return toast.error(t("Fill all fields"));
    const pe = passwordError(form.password); if (pe) return toast.error(pe);
    try { await api.post("/users", form); toast.success(t("User created")); setShow(false); setForm({ name: "", email: "", password: "", role: "mechanic" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setRole = async (id, role) => { try { await api.put(`/users/${id}/role`, { role }); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (id) => { if (!window.confirm(t("Delete this user?"))) return; try { await api.delete(`/users/${id}`); load(); } catch (e) { toast.error(apiError(e)); } };
  const reset2fa = async (id) => { if (!window.confirm(t("Reset 2FA? User re-enrolls on next login."))) return; try { await api.post(`/users/${id}/2fa-reset`); toast.success(t("2FA reset")); load(); } catch (e) { toast.error(apiError(e)); } };

  const inp = "bg-[#1c1c1f] border border-zinc-800 text-white rounded-lg px-3 py-2 focus:border-[#F26722] focus:outline-none";

  return (
    <div className="min-h-screen bg-[#0A0A0A] max-w-md mx-auto sm:max-w-2xl md:max-w-4xl border-x border-zinc-800/40 pb-10">
      <div className="sticky top-0 z-40 bg-[#0A0A0A]/90 backdrop-blur-md px-4 py-4 border-b border-zinc-800/70 flex items-center justify-between">
        <button onClick={() => nav("/profile")} className="flex items-center gap-2 text-zinc-300"><ArrowLeft size={20} /> {t("Profile")}</button>
        <BrandMark />
      </div>
      <div className="px-4 pt-4">
        <div className="flex items-center justify-between">
          <div><h1 className="text-2xl font-extrabold text-white">{t("Team")}</h1><p className="text-zinc-400 text-sm">{t("Users in")} {user?.company_name}</p></div>
          <button onClick={() => setShow(!show)} className="bg-[#F26722] text-white font-bold px-3 py-2 rounded-lg text-sm flex items-center gap-1" data-testid="new-team-user-btn">{show ? <X size={16} /> : <Plus size={16} />} {show ? t("Close") : t("Add")}</button>
        </div>
        {show && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-xl p-4 mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input className={inp} placeholder={t("Name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-name-input" />
            <input className={inp} placeholder={t("Email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="team-email-input" />
            <input className={inp} type="password" placeholder={t("Password (10+ chars, letters & numbers)")} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="team-password-input" />
            <select className={inp} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="team-role-select">{roles.map((r) => <option key={r} value={r}>{t(r === "mechanic" ? "mechanic" : r)}</option>)}</select>
            <button onClick={create} className="bg-[#F26722] text-white font-bold rounded-lg py-2 sm:col-span-2" data-testid="team-create-btn">{t("Create user")}</button>
          </div>
        )}
        <div className="mt-4 space-y-2">
          {users.map((u) => (
            <div key={u.id} className="flex items-center justify-between bg-[#18181B] border border-zinc-800 rounded-xl p-3.5">
              <div><p className="font-bold text-white">{u.name}</p><p className="text-xs text-zinc-500">{u.email} · {u.mfa_enabled ? t("2FA on") : t("2FA not set")}</p></div>
              <div className="flex items-center gap-2">
                <select value={u.role} onChange={(e) => setRole(u.id, e.target.value)} className="bg-[#141416] border border-zinc-800 text-zinc-200 text-xs rounded-lg px-2 py-1" disabled={u.id === user?.id}>
                  {["manager", "mechanic", "office"].map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <button onClick={() => reset2fa(u.id)} className="text-amber-400" title="Reset 2FA"><ShieldOff size={16} /></button>
                {u.id !== user?.id && <button onClick={() => del(u.id)} className="text-red-400"><Trash2 size={16} /></button>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
